import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import { themeFontFamily, themeColor, themeFontWeight, themeFontSize } from '../../util/themeLenses'
import { FONT_SIZES, FONT_WEIGHTS, COLORS } from '../../theme'

import {
    marginBottom,
    marginTop,
    marginRight,
    marginLeft,
    display,
    width,
    height,
    lineHeight,
    overflow,
    borderStyle,
} from '../../util/styles'

const shadow = props => props.withShadow ? '0px 3px 5px 0px rgba(0, 0, 0, 0.2)' : 'none'

const styles = css`
    cursor: pointer;
    outline: none;
    ${overflow}
    ${display}
    ${marginBottom}
    ${marginTop}
    ${marginRight}
    ${marginLeft}
    ${borderStyle}
    ${lineHeight}
    ${width}
    ${height}

    color: ${({color, theme}) => themeColor(color || theme.components.button.color) };
    border-color: ${({borderColor, theme}) => themeColor(borderColor || theme.components.button.borderColor) };
    background-color: ${({backgroundColor, theme}) => themeColor(backgroundColor || theme.components.button.backgroundColor) };
    font-size: ${({fontSize, theme}) => themeFontSize(fontSize || theme.components.button.fontSize) };
    font-weight: ${({fontWeight, theme}) => themeFontWeight(fontWeight || theme.components.button.fontWeight) };
    text-transform: ${({textTransform, theme}) => textTransform || theme.components.button.textTransform };
    min-height: ${({minHeight, theme}) => minHeight || theme.components.button.minHeight };
    border-width: ${({borderWidth, theme}) => borderWidth || theme.components.button.borderWidth };
    padding: ${({padding, theme}) => padding || theme.components.button.padding };
    height: ${({height, theme}) => height || theme.components.button.height };
    border-radius: ${({borderRadius, theme}) => borderRadius || theme.components.button.borderRadius };
    box-shadow: ${shadow};
    ${themeFontFamily()}
    &:active {
        background-color: ${({ activeBackgroundColor, theme }) => themeColor(activeBackgroundColor || theme.components.button.activeBackgroundColor)};
    }

    @media (hover: hover) {
        &:hover {
            background-color: ${({hoverBackgroundColor, theme}) => themeColor(hoverBackgroundColor || theme.components.button.hoverBackgroundColor) };
            border-color: ${({ hoverBorderColor, theme }) => themeColor(hoverBorderColor || theme.components.button.hoverBorderColor)};
            color: ${({ hoverColor, theme }) => themeColor(hoverColor || theme.components.button.hoverColor)};
        }
    }

    &:disabled {
        background-color: ${({disabledBackgroundColor, theme}) => themeColor(disabledBackgroundColor || theme.components.button.disabledBackgroundColor)};
        cursor: not-allowed;
    }
    &:focus-visible {
        border: 1.5px solid ${themeColor('focusColor')};
        outline: 1.5px solid ${themeColor('focusColor')};
    }
`

export { FONT_SIZES, FONT_WEIGHTS, COLORS }

const Button = styled.button.attrs(props => ({'data-test': props.dataTest, tabIndex: 0}))`${styles}`
Button.propTypes = {
    fontWeight: PropTypes.oneOfType([
        PropTypes.oneOf(FONT_WEIGHTS),
        PropTypes.arrayOf(PropTypes.oneOf(FONT_WEIGHTS)),
    ]),
    fontSize: PropTypes.oneOfType([
        PropTypes.oneOf(FONT_SIZES),
        PropTypes.arrayOf(PropTypes.oneOf(FONT_SIZES)),
    ]),
    color: PropTypes.oneOfType([
        PropTypes.oneOf(['inherit', ...COLORS]),
        PropTypes.arrayOf(PropTypes.oneOf(COLORS)),
    ]),
    minHeight: PropTypes.string,
    textTransform: PropTypes.string,
    borderRadius: PropTypes.string,
    borderStyle: PropTypes.string,
    withShadow: PropTypes.bool,
    disabledBackgroundColor: PropTypes.oneOf(['inherit', ...COLORS]),
    hoverBackgroundColor: PropTypes.oneOf(['inherit', ...COLORS]),
    hoverColor: PropTypes.oneOf(['inherit', ...COLORS]),
    activeColor: PropTypes.oneOf(['inherit', ...COLORS]),
    backgroundColor: PropTypes.oneOf(['inherit', ...COLORS]),
    borderWidth: PropTypes.string,
    borderColor: PropTypes.oneOf(['inherit', ...COLORS]),
}

Button.defaultProps = {
    borderStyle: 'solid',
    shadow: 'none',
    display: 'inline-block',
    width: 'auto',
}

Button.bordered = styled(Button)`
    border-width: ${({borderWidth, theme}) => borderWidth || theme.components.buttonBordered.borderWidth };
    border-color: ${({borderColor, theme}) => themeColor(borderColor || theme.components.buttonBordered.borderColor) };
    background-color: ${({backgroundColor, theme}) => themeColor(backgroundColor || theme.components.buttonBordered.backgroundColor) };
    color: ${({color, theme}) => themeColor(color || theme.components.buttonBordered.color) };


    @media (hover: hover) {
        &:hover {
            background-color: ${({hoverBackgroundColor, theme}) => themeColor(hoverBackgroundColor || theme.components.buttonBordered.hoverBackgroundColor) };
            color: ${({hoverColor, theme}) => themeColor(hoverColor || theme.components.buttonBordered.hoverColor) };
            border-color: ${({hoverBorderColor, theme}) => themeColor(hoverBorderColor || theme.components.buttonBordered.hoverBorderColor) };

        }
    }

    &:focus-visible {
        border: 1px solid ${themeColor('focusColor')};
        outline: 1px solid ${themeColor('focusColor')};
    }

`

Button.unstyled = styled(Button)`
    background-color: ${({backgroundColor, theme}) => themeColor(backgroundColor || theme.components.buttonUnstyled.backgroundColor) };
    color: ${({color, theme}) => themeColor(color || theme.components.buttonUnstyled.color) };
    font-size: ${({fontSize, theme}) => themeFontSize(fontSize || theme.components.buttonUnstyled.fontSize) };
    font-weight: ${({fontWeight, theme}) => themeFontWeight(fontWeight || theme.components.buttonUnstyled.fontWeight) };
    padding: ${({padding, theme}) => padding || theme.components.buttonUnstyled.padding };
    height: ${({height, theme}) => height || theme.components.buttonUnstyled.height };

    @media (hover: hover) {
        &:hover {
            background-color: ${({hoverBackgroundColor, theme}) => themeColor(hoverBackgroundColor || theme.components.buttonUnstyled.hoverBackgroundColor) };
            color: ${({hoverColor, theme}) => themeColor(hoverColor || theme.components.buttonUnstyled.hoverColor) };
        }
    }

    &:disabled {
        background-color: ${({disabledBackgroundColor, theme}) => themeColor(disabledBackgroundColor || theme.components.buttonUnstyled.disabledBackgroundColor) };
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 1px solid ${themeColor('focusColor')};
        padding: 0.2em;
    }
`

export default Button

